import { PROPERTY_CACHE_KEY } from 'config/constants';
import {
    includes,
    isEmpty,
    isNull,
    isUndefined,
    pick,
    get,
    toArray,
} from 'lodash';

import PropTypes from 'prop-types';

import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Shortcuts } from 'react-shortcuts';
import { connectRequest } from 'redux-query';
import { createColorScale } from 'utils/color';
import { getPageDetails, PAGE_NAME_ONLY } from 'utils/pageTitleHelpers';
import { keyMap, keyMap2, default as shortcutManager } from 'config/shortcuts';
import { setPageTitle } from 'state/browser';
import { getAccessListsForProperties } from 'state/requests';
import { clientLoaded } from 'state/user/reducer';
import Topbar from 'components/app/topbar/Topbar';
import styles from 'components/app/App.module.css';
import 'styles/index.scss';
import urlBuilder from 'utils/urlBuilder';
import { clearCookie } from 'utils/cookie';
import { ONE_HOUR_REFRESH } from './constants';
import { withRouter } from 'react-router-dom';
import { RouteUrls } from 'routes/RouteConstants';

import 'react-reflex/styles.css';

import { getHeartbeat } from 'waypoint-requests';
import {
    applicationNeedsUpdate,
    saveApplicationVersion,
    updateApplicationVersion,
} from 'waypoint-utils';
import {
    hasOwnershipPercentageEnabled,
    selectCurrentUser,
} from 'state/user/selectors';
import { selectUsersById } from 'state/users/selectors';
import { EntityOwnershipProvider } from '../../contexts/entity-ownership/EntityOwnershipProvider';
import { PermissionsGroupsProvider, allAccessPermissions } from 'contexts';

class App extends React.Component {
    static propTypes = {
        notReady: PropTypes.bool,
        loadingUsersWithAccess: PropTypes.bool,
        hasTasks: PropTypes.bool,
        user: PropTypes.object,
        browser: PropTypes.object,
        children: PropTypes.element,
        params: PropTypes.object,
        location: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
        properties: PropTypes.object.isRequired,
        setPageTitle: PropTypes.func.isRequired,
        isOwnershipPercentageEnabled: PropTypes.bool,
    };

    static contextTypes = {
        router: PropTypes.object.isRequired,
        browser: PropTypes.object,
    };

    static childContextTypes = {
        colors: PropTypes.func,
        location: PropTypes.object,
        shortcuts: PropTypes.object,
    };

    authCheckInterval = null;
    applicationVersionInterval = null;

    getChildContext() {
        const colors = createColorScale();
        window.colors = colors;
        return {
            colors,
            location: this.props.location,
        };
    }

    componentDidMount() {
        this.unlistenToApplicationVersionUpdateRouteChanges =
            this.props.history.listen(() => {
                if (!applicationNeedsUpdate()) {
                    return;
                }

                updateApplicationVersion();

                // Refresh the application and try to blow away the cache.
                window.location.reload(true);
            });

        this.authCheckInterval = setInterval(() => {
            if (!document.cookie.includes('CLIENT_ID_COOKIE')) {
                const loginUrl = urlBuilder.addQuery(
                    urlBuilder.locationOrigin() + '/login/index.html',
                    {
                        state: window.location.href,
                        idle: true,
                    }
                );

                clearCookie('XSRF-TOKEN');
                clearCookie('CLIENT_ID_COOKIE');

                this.props.history.replace(loginUrl);
            }
        }, ONE_HOUR_REFRESH);

        this.applicationVersionInterval = setInterval(
            this._updateApplicationVersion,
            1000 * 60
        ); // 60 seconds

        const { user } = this.props;

        if (!isEmpty(user) && clientLoaded(user.clientObj)) {
            const loadingScreen = document.querySelector('.loader-container');
            if (loadingScreen) {
                loadingScreen.remove();
                window.loading = false;
            }
        }
    }

    _updateApplicationVersion = async () => {
        try {
            const { application_version: newApplicationVersion } =
                await getHeartbeat();
            saveApplicationVersion(newApplicationVersion);
        } catch (e) {
            console.error('Failed to update application version', e);
        }
    };

    componentWillUpdate(nextProps) {
        if (this.propertiesAvailable(nextProps)) {
            // when property and group details become available
            if (
                !this.props.browser.currentUrl ||
                this.isDifferentContent(nextProps)
            ) {
                const entityName = this.getEntityName(nextProps);
                // NOTE: hasTasks moves through 5 layers of indirection before choosing
                //       the alternate page title text.
                // TODO (Nicholas): Create a standard "Page" component which handles
                //                  page title changes, GA events, and layout, to
                //                  avoid centralizing such things
                this.props.setPageTitle(
                    nextProps.location.pathname,
                    entityName,
                    nextProps.hasTasks
                );
            }

            // If Google Analytics is switched on and a page change is been detected
            // TODO: Remove global dependency on this window.urlChange trigger
            //       which is set in the global route onChange / onEnter callbacks
            if (!isUndefined(window.ga) && window.urlChange) {
                this.prepareGoogleAnalyticsPayloadAndSend(nextProps); // google analytics - set details and send pageview hit
            }
        }
    }

    componentWillUnmount() {
        if (this.authCheckInterval) {
            clearInterval(this.authCheckInterval);
        }

        if (this.applicationVersionInterval) {
            clearInterval(this.applicationVersionInterval);
        }

        if (this.unlistenToApplicationVersionUpdateRouteChanges) {
            this.unlistenToApplicationVersionUpdateRouteChanges();
        }
    }

    // getEntityName is to retrieve the property/group/project name
    // - will need to be updated when more entities are created in the future
    // TODO (Nicholas): gate these checks better, maybe with brototype: https://github.com/letsgetrandy/brototype
    getEntityName = (props) => {
        const { notReady, location, properties } = props;
        const { pathname } = location;

        const pureid = new URLSearchParams(location.search).get('pureid');
        if (notReady) {
            return '';
        }
        if (
            includes(pathname, 'property') &&
            get(properties, `[${PROPERTY_CACHE_KEY + pureid}]`)
        ) {
            return get(
                properties,
                `[${PROPERTY_CACHE_KEY + pureid}].display_name`
            );
        }

        return '';
    };

    propertiesAvailable = (props) => {
        return !isNull(props.properties) && !isEmpty(props.properties);
    };

    isDifferentContent = (props) => {
        const pureid = new URLSearchParams(this.props.location.search).get(
            'pureid'
        );
        const newPureid = new URLSearchParams(props.location.search).get(
            'pureid'
        );
        return (
            this.props.browser.currentUrl !== props.location.pathname ||
            pureid !== newPureid
        );
    };

    prepareGoogleAnalyticsPayloadAndSend = (props) => {
        const pureid = new URLSearchParams(props.location.search).get('pureid');
        const accountCode = new URLSearchParams(props.location.search).get(
            'account_code'
        );

        // Why pass props through here when this.props works?
        const { hasTasks } = props;
        const propertyIdOrGroupIdDimension = includes(
            props.location.pathname,
            'group'
        )
            ? 'dimension4'
            : 'dimension3';
        window.ga('set', propertyIdOrGroupIdDimension, pureid); // set groupId or propertyId
        const propertyNameOrGroupNameDimension = includes(
            props.location.pathname,
            'group'
        )
            ? 'dimension9'
            : 'dimension8';
        window.ga(
            'set',
            propertyNameOrGroupNameDimension,
            this.getEntityName(props)
        ); // set group or property name
        if (accountCode) {
            window.ga('set', 'dimension5', accountCode); // set expense code
        }
        window.ga(
            'set',
            'dimension12',
            getPageDetails(props.location.pathname, PAGE_NAME_ONLY, hasTasks)
        );
        // TODO (ALEX): add account code name to the google analytics payload
        //  - this may require connecting additional components to the redux store as the data is obtained further down the component stack
        window.ga('send', 'pageview');
        window.ga('set', {
            dimension3: null, // unset propertyId
            dimension4: null, // unset groupId
            dimension8: null, // unset propertyName
            dimension9: null, // unset groupName
        });
        window.urlChange = false; // set flag to show that pageview has been sent
    };

    render() {
        const {
            browser,
            history,
            bootstrapped,
            isOwnershipPercentageEnabled,
            userPermissions,
        } = this.props;

        const currentPath = this.props.location.pathname;
        const hideGlobalFilterBar =
            currentPath.startsWith(RouteUrls.PROPERTY) ||
            currentPath.startsWith(RouteUrls.SETTINGS) ||
            currentPath.startsWith(RouteUrls.ENTITY_REPORT_EDITOR) ||
            currentPath.startsWith(RouteUrls.FINANCIALS_VARIANCE_REPORTING);

        const hideDataLevelSelection =
            !currentPath.startsWith(RouteUrls.LEASES_PERFORMANCE_OVERVIEW) &&
            !currentPath.startsWith(RouteUrls.ANALYTICS_RANKING) &&
            !currentPath.startsWith(RouteUrls.LEASE_RENT_ROLL) &&
            !currentPath.startsWith(RouteUrls.LEASES_EXPIRATIONS_SCHEDULE) &&
            !currentPath.startsWith(
                RouteUrls.FINANCIALS_COMPARATIVE_INCOME_STATEMENT
            ) &&
            !currentPath.startsWith(RouteUrls.FINANCIALS_CROSSTAB) &&
            !currentPath.startsWith(RouteUrls.FINANCIALS_BALANCE_SHEET) &&
            !currentPath.startsWith(RouteUrls.LEASES_TENANTS) &&
            !currentPath.startsWith(
                RouteUrls.ANALYTICS_BALANCE_SHEET_OVERVIEW
            ) &&
            !currentPath.startsWith(RouteUrls.ANALYTICS_FINANCIAL_OVERVIEW) &&
            !currentPath.startsWith(RouteUrls.PROPERTY_PROFILE_DETAILS);

        const innerHeight = hideGlobalFilterBar ? 50 : 100;

        return (
            <Shortcuts
                name="APP"
                global={true}
                stopPropagation={false}
                handler={(action) => {
                    switch (action) {
                        case 'TOGGLE_KEYMAP':
                            if (keyMap === shortcutManager.getAllShortcuts()) {
                                shortcutManager.setKeymap(keyMap2);
                            } else {
                                shortcutManager.setKeymap(keyMap);
                            }
                            break;
                        case 'PROPERTIES':
                            history.push('/search');
                            break;
                        case 'PORTFOLIO':
                            history.push('/portfolio');
                            break;
                        default:
                    }
                }}
            >
                <div className={styles.appContainer}>
                    <div className={styles.container}>
                        {bootstrapped && (
                            <PermissionsGroupsProvider
                                userPermissions={userPermissions}
                            >
                                <EntityOwnershipProvider
                                    isDisabled={!isOwnershipPercentageEnabled}
                                >
                                    <Topbar
                                        hideGlobalFilterBar={
                                            hideGlobalFilterBar
                                        }
                                        hideDataLevelSelection={
                                            !isOwnershipPercentageEnabled ||
                                            hideDataLevelSelection
                                        }
                                    />
                                </EntityOwnershipProvider>
                            </PermissionsGroupsProvider>
                        )}
                        <div
                            style={{
                                position: 'fixed',
                                top: `${innerHeight}px`,
                                width: browser.width,
                                height: browser.height - innerHeight + 'px',
                                maxWidth: browser.width,
                                display: 'flex',
                            }}
                        >
                            {this.props.children}
                        </div>
                    </div>
                </div>
            </Shortcuts>
        );
    }
}

// NOTE: very strange logic
const mapState = (state) => {
    let notReady = true;
    let loadingUsersWithAccess = false; // probably need to delete all the related code and not worry about it

    const { properties, user, users } = state;
    const { clientObj } = user;
    const { id: clientId } = clientObj;

    if (
        !isEmpty(properties) ||
        !isEmpty(users) ||
        !isUndefined(clientObj.configuration)
    ) {
        notReady = false;
        // NOTE: fetch all UsersWithAccess in properties
        if (
            toArray(properties).findIndex(
                (property) => !property.usersWithAccess
            ) === -1
        ) {
            loadingUsersWithAccess = false;
        }
    }

    const slices = pick(state, [
        'user',
        'browser',
        'favorites',
        'properties',
        'portfolioActive',
    ]);

    // NOTE: can't drop booleans into pick statements
    const hasTasks = state.user.configuration.FEATURE_TASKS;

    if (!hasTasks) {
        // we only load the users who have access to properties for Tasks 2.0
        // it's used to select the assignee on a task for a property
        loadingUsersWithAccess = false;
    }

    const currentUser = selectCurrentUser(state);
    const currentUserFeaturePermissions = selectUsersById(state, {
        userIds: [currentUser?.id],
    })[0]?.featurePermissions;

    return {
        clientId,
        notReady,
        loadingUsersWithAccess,
        hasTasks,
        isOwnershipPercentageEnabled: hasOwnershipPercentageEnabled(state),
        userPermissions: currentUserFeaturePermissions?.length
            ? currentUserFeaturePermissions
            : allAccessPermissions,
        ...slices,
    };
};

const mapDispatch = (dispatch) => {
    return {
        setPageTitle: (pageName, subject, useAlternate) =>
            dispatch(setPageTitle(pageName, subject, useAlternate)),
    };
};

export default withRouter(
    compose(
        connect(mapState, mapDispatch),
        connectRequest((props) => {
            const { clientId, notReady, loadingUsersWithAccess } = props;

            // NOTE (daniel): what does this mean?
            if (!!global.jasmine) {
                return undefined;
            }

            const configs = [];
            // Redux-form uses null as the default cancellation value
            if (loadingUsersWithAccess && !notReady) {
                const { properties } = props;

                const propertyIds = toArray(properties).map(
                    (property) => property.id
                );

                // NOTE: fetch all UsersWithAccess in properties
                configs.push(
                    getAccessListsForProperties({
                        clientId,
                        propertyIds,
                    })
                );

                return configs;
            }

            return configs.length === 0 ? undefined : configs;
        })
    )(App)
);
